// src/components/mitchRoom/revenueAnalytics/UpcomingPayments.js
//
// What existing subscriptions are still due to bill this month.
//
// This is COMMITTED money, not a forecast. It is read from each active
// subscription's next_billing_date, so it knows nothing about people who
// haven't bought yet — which in August is most of the money. That's why the
// headline is framed as a FLOOR for the month, never a projection.
import React from 'react';
import { CalendarClock, AlertTriangle, Info } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { Skeleton } from 'components/ui/skeleton';
import { formatCurrency, formatPeriod, formatAxisCurrency } from 'utils/format';

export default function UpcomingPayments({ upcoming, loading }) {
  if (loading && !upcoming) {
    return (
      <div className="rounded-token-large border border-borderGrey bg-white p-4">
        <Skeleton className="mb-3 h-6 w-72" />
        <Skeleton className="h-[220px] w-full" />
      </div>
    );
  }

  if (!upcoming) return null;

  const {
    month,
    mtd_gbp: mtd = 0,
    expected_gbp: expected = 0,
    expected_gbp_adjusted: adjusted = null,
    first_real_charge_subs: firstRealCharge = 0,
    expected_subs: expectedSubs = 0,
    free_subs: freeSubs = 0,
    month_floor_gbp: floor = 0,
    by_product: byProduct = [],
    by_day: byDay = [],
    cancelling_gbp: cancelling = 0,
    cancelling_subs: cancellingSubs = 0,
    at_risk_gbp: atRisk = 0,
    at_risk_subs: atRiskSubs = 0,
    stale_subs: staleSubs = 0,
    pending_cancel_source: pendingSource = null,
  } = upcoming;

  const today = new Date().toISOString().slice(0, 10);
  // Biggest scheduled day gets called out — for August that's the combo
  // cohort's first monthly on deadline day.
  const peak = byDay.reduce((a, b) => (b.gbp > (a?.gbp ?? 0) ? b : a), null);

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <div
          className="rounded-token-large border border-borderGrey border-l-4 bg-white p-3"
          style={{ borderLeftColor: '#59A14F' }}
        >
          <p className="text-token-sm text-muted-foreground">Banked so far</p>
          <p className="text-token-lg font-semibold">{formatCurrency(mtd, { whole: true })}</p>
          <p className="text-token-xs text-muted-foreground">{formatPeriod(month)} to date</p>
        </div>
        <div
          className="rounded-token-large border border-borderGrey border-l-4 bg-white p-3"
          style={{ borderLeftColor: '#1072CA' }}
          title="Existing subscriptions with a billing date still to come this month. Excludes anything pending cancellation."
        >
          <div className="mb-1 flex items-center gap-1">
            <CalendarClock size={13} className="shrink-0" style={{ color: '#1072CA' }} aria-hidden="true" />
            <span className="text-token-sm text-muted-foreground">Still to bill</span>
          </div>
          <p className="text-token-lg font-semibold">
            {formatCurrency(expected, { whole: true })}
            {adjusted !== null && adjusted !== undefined && (
              <span className="ml-1 text-token-sm font-normal text-muted-foreground">
                → {formatCurrency(adjusted, { whole: true })} expected
              </span>
            )}
          </p>
          <p className="text-token-xs text-muted-foreground">
            {expectedSubs} subscription{expectedSubs === 1 ? '' : 's'}
            {freeSubs > 0 ? ` (${freeSubs} free month)` : ''}
            {firstRealCharge > 0 ? ` · ${firstRealCharge} first real charge` : ''}
          </p>
        </div>
        <div
          className="rounded-token-large border border-borderGrey border-l-4 bg-white p-3"
          style={{ borderLeftColor: '#254A73' }}
          title="Banked + already-scheduled renewals. A floor: it cannot include anyone who buys between now and month end."
        >
          <div className="mb-1 flex items-center gap-1">
            <span className="text-token-sm text-muted-foreground">Month floor</span>
            <Info size={11} className="shrink-0 text-muted-foreground" aria-hidden="true" />
          </div>
          <p className="text-token-2xl font-semibold">
            {formatCurrency(floor, { whole: true })}
          </p>
          <p className="text-token-xs text-muted-foreground">before any new sales</p>
        </div>
        <div
          className="rounded-token-large border border-borderGrey border-l-4 bg-white p-3"
          style={{ borderLeftColor: cancelling > 0 || atRisk > 0 ? '#E15759' : '#9e9e9e' }}
          title="Due in this window but pending cancellation, so it will not arrive. Past-due subscriptions are owed but have already failed collection once."
        >
          <div className="mb-1 flex items-center gap-1">
            <AlertTriangle size={13} className="shrink-0 text-muted-foreground" aria-hidden="true" />
            <span className="text-token-sm text-muted-foreground">Won&apos;t arrive</span>
          </div>
          <p className="text-token-lg font-semibold">
            {formatCurrency(cancelling, { whole: true })}
          </p>
          <p className="text-token-xs text-muted-foreground">
            {cancellingSubs} cancelling{pendingSource === 'cancel_at_period_end' ? '' : ' (proxy)'}
            {atRiskSubs > 0 ? ` · ${atRiskSubs} past due (${formatCurrency(atRisk, { whole: true })})` : ''}
            {staleSubs > 0 ? ` · ${staleSubs} stale` : ''}
          </p>
        </div>
      </div>

      <div className="rounded-token-large border border-borderGrey bg-white p-4">
        <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="text-token-xl font-semibold">Scheduled renewals — rest of {formatPeriod(month)}</h3>
          {peak && (
            <p className="text-token-xs text-muted-foreground">
              Biggest day: {formatPeriod(peak.date)} · {peak.subs} subs ·{' '}
              {formatCurrency(peak.gbp, { whole: true })}
            </p>
          )}
        </div>

        {byDay.length === 0 ? (
          <p className="py-6 text-center text-token-md text-muted-foreground">
            Nothing further scheduled this month.
          </p>
        ) : (
          <div className="h-[240px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={byDay} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#DADADA" vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11 }}
                  tickFormatter={(d) => formatPeriod(d)}
                  interval="preserveStartEnd"
                  minTickGap={16}
                />
                <YAxis tickFormatter={formatAxisCurrency} tick={{ fontSize: 11 }} width={58} />
                <Tooltip
                  formatter={(v, n) =>
                    n === 'gbp' ? [formatCurrency(v), 'Scheduled'] : [v, n]
                  }
                  labelFormatter={(d, payload) => {
                    const row = payload?.[0]?.payload;
                    return `${formatPeriod(d)}${row ? ` · ${row.subs} subscriptions` : ''}`;
                  }}
                />
                <Bar dataKey="gbp" radius={[3, 3, 0, 0]} isAnimationActive={false}>
                  {byDay.map((d) => (
                    <Cell
                      key={d.date}
                      // Today's remaining billings are dimmer — part of the
                      // day has already happened and is in "banked".
                      fill={d.date === today ? '#9ec5e8' : '#1072CA'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {byProduct.length > 0 && (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-token-md">
              <thead>
                <tr>
                  <th className="border-b border-borderGrey p-2 text-left font-semibold">Product</th>
                  <th className="border-b border-borderGrey p-2 text-right font-semibold">Subs</th>
                  <th className="border-b border-borderGrey p-2 text-right font-semibold">Expected</th>
                </tr>
              </thead>
              <tbody>
                {byProduct.map((p) => (
                  <tr key={`${p.category}-${p.subscription_type}`}>
                    <td className="border-b border-borderGrey p-2">{p.category}</td>
                    <td className="border-b border-borderGrey p-2 text-right tabular-nums">
                      {p.subs}
                    </td>
                    <td className="border-b border-borderGrey p-2 text-right tabular-nums">
                      {formatCurrency(p.gbp)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="mt-2 text-token-xs text-muted-foreground">
          Committed billings only — read from each active subscription&apos;s next
          billing date. It cannot know about sales that haven&apos;t happened, so
          treat the month floor as a minimum, not a forecast; the
          &ldquo;scheduled → expected&rdquo; figure applies the forecast&apos;s
          survival and collection haircut (first real charges collect at a lower
          rate). Stale = active but with a billing date already in the past
          (in-flight Stripe renewals, legacy £0 WooCommerce passes) — never counted.
          The recurring half of a Pre-Season combo bills as a plain &ldquo;Monthly
          Subscription&rdquo;, so combo first payments appear on that line.
        </p>
      </div>
    </div>
  );
}
